import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DocumentValidationGuide = () => {
  const [activeTab, setActiveTab] = useState('government-id');

  const validationGuides = {
    'government-id': {
      title: 'Government ID Proof',
      icon: 'CreditCard',
      requirements: [
        'Clear, high-resolution image (minimum 300 DPI)',
        'All four corners of the document must be visible',
        'Text should be clearly readable without blur',
        'No shadows or glare on the document',
        'Document should be flat, not folded or bent'
      ],
      acceptedDocs: [
        'Aadhar Card (front and back)',
        'Passport (photo page)',
        'Driving License',
        'Voter ID Card',
        'PAN Card'
      ],
      commonErrors: [
        'Blurry or low-quality images',
        'Partial document visibility',
        'Shadows covering important details',
        'Expired documents',
        'Damaged or torn documents'
      ]
    },
    'passport-photo': {
      title: 'Passport Photo',
      icon: 'User',
      requirements: [
        'Recent photo taken within last 6 months',
        'Clear face visibility with neutral expression',
        'Plain white or light-colored background',
        'Good lighting with no shadows on face',
        'Photo should be in color, not black and white'
      ],
      acceptedDocs: [
        'Professional passport-size photograph',
        'High-quality selfie with plain background',
        'Studio photograph meeting passport standards'
      ],
      commonErrors: [
        'Dark or poor lighting',
        'Busy or colored backgrounds',
        'Sunglasses or face coverings',
        'Low resolution or pixelated images',
        'Old photographs (over 6 months)'
      ]
    },
    'resume': {
      title: 'Resume Upload',
      icon: 'FileText',
      requirements: [
        'Updated resume with recent information',
        'PDF format preferred for best compatibility',
        'File size should be under 5MB',
        'Professional formatting and layout',
        'Include contact information and skills'
      ],
      acceptedDocs: [
        'PDF format resume',
        'Microsoft Word document (.docx)',
        'Plain text format (.txt)'
      ],
      commonErrors: [
        'Outdated or incomplete information',
        'Poor formatting or layout',
        'File size exceeding limits',
        'Corrupted or unreadable files',
        'Missing contact information'
      ]
    }
  };

  const tabs = [
    { id: 'government-id', label: 'ID Proof', icon: 'CreditCard' },
    { id: 'passport-photo', label: 'Photo', icon: 'User' },
    { id: 'resume', label: 'Resume', icon: 'FileText' }
  ];

  const activeGuide = validationGuides[activeTab];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-muted/30 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Info" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Document Upload Guidelines</h3>
        </div>
        <p className="text-sm text-text-secondary mt-1">
          Follow these guidelines to ensure your documents are accepted quickly
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-smooth flex-1
              ${activeTab === tab.id 
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-foreground hover:bg-muted/50'
              }
            `}
          >
            <Icon name={tab.icon} size={16} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Requirements */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <span>Requirements</span>
          </h4>
          <ul className="space-y-2">
            {activeGuide.requirements.map((requirement, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm">
                <Icon name="Check" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                <span className="text-text-secondary">{requirement}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Accepted Documents */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
            <Icon name="FileCheck" size={16} color="var(--color-primary)" />
            <span>Accepted Documents</span>
          </h4>
          <ul className="space-y-2">
            {activeGuide.acceptedDocs.map((doc, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm">
                <Icon name="Dot" size={14} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <span className="text-text-secondary">{doc}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Common Errors */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
            <span>Common Errors to Avoid</span>
          </h4>
          <ul className="space-y-2">
            {activeGuide.commonErrors.map((error, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm">
                <Icon name="X" size={14} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                <span className="text-text-secondary">{error}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Help Notice */}
        <div className="flex items-start space-x-2 p-4 bg-primary/10 border border-primary/20 rounded-md">
          <Icon name="HelpCircle" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-primary">Need Help?</p>
            <p className="text-xs text-text-secondary">
              If you're having trouble uploading documents, contact our support team at{' '}
              <a href="mailto:solvarsity@solutioneers.in" className="text-primary hover:underline">
                solvarsity@solutioneers.in
              </a>{' '}
              or call{' '}
              <a href="tel:+919454588858" className="text-primary hover:underline">
                +919454588858
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentValidationGuide;